import {
  Box,
  Flex,
  Icon,
  IconButton,
  InterfaceWarningIcon,
  Text,
  useColorScheme,
} from '@grammarly/design-system'

import styles from './ErrorBox.module.scss'
import { ErrorBoxInlineAction, type Props as InlineActionProps } from './InlineAction'
import { CloseIcon } from '../icons'

/**
 * Props for ErrorBox component.
 * Everything is optional, but at least one of the fields has to be set.
 */
export type ErrorBoxProps =
  | { title: string; content?: string; action?: InlineActionProps; closeButton?: VoidFunction }
  | { title?: string; content: string; action?: InlineActionProps; closeButton?: VoidFunction }
  | { title?: string; content?: string; action: InlineActionProps; closeButton?: VoidFunction }

/**
 * A styled error display component with optional title, content, and action button.
 * Features a warning icon and supports light/dark theme color schemes.
 * Commonly used as fallback UI in error boundaries and error states.
 */
export function ErrorBox({ action, title, content, closeButton }: ErrorBoxProps) {
  const { scheme } = useColorScheme()
  const bgColor = scheme === 'dark' ? 'gold-20' : 'gold-0'
  const textColor = scheme === 'dark' ? 'dark-default' : 'base-default'

  return (
    <Box data-testid="ErrorBox" bgColor={bgColor} borderRadius={2} padding={3}>
      <Flex gap={2}>
        <Flex>
          <Icon
            className={styles.warningIcon}
            size="medium"
            icon={InterfaceWarningIcon}
            accessibilityLabel=""
          />
        </Flex>
        <Flex direction="column">
          <Flex direction="row" justify="space-between">
            {title && (
              <Text as="p" weight="semibold" color={textColor} variant="text-xsmall">
                {title}
              </Text>
            )}
            {closeButton && (
              <IconButton
                size="small"
                variant="tertiary"
                accessibilityLabel="Close"
                className={styles.closeIcon}
                icon={CloseIcon}
                onClick={closeButton}
              />
            )}
          </Flex>

          {(content || action) && (
            <Text as="p" weight="normal" color={textColor} variant="text-xsmall">
              {content && `${content} `}
              {action && <ErrorBoxInlineAction {...action} />}
            </Text>
          )}
        </Flex>
      </Flex>
    </Box>
  )
}
